import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  BorderRadiusCorners,
  Box,
  Divider,
  Icon,
  SearchField,
  Text,
  Touchable,
  matchText,
} from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import ScrollableList from 'modules/core/components/ScrollableList'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import UnsafeRadioInput from 'modules/shift_management/components/ShiftManagement/UnsafeRadioInput'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { trackAddDefaultShiftStart } from 'modules/shift_management/lib/trackEvents'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

import ButtonAddNew from './ButtonAddNew'
import DefaultShiftItem from './DefaultShiftItem'

interface Props {
  forms: UnassignedShiftForm[]
  isSelected: boolean
  onToggleShift: (id: string) => void
  onSelectDefaultShiftType: () => void
  setTrackSearch?: React.Dispatch<React.SetStateAction<string>>
  isAutomationEnabled?: boolean
  locationFilterValue: Field
  workAreaFilterValue: Field
  isComplementaryHoursEnabled?: boolean
}

const NewDefaultShiftForm = ({
  forms,
  isSelected,
  onToggleShift,
  onSelectDefaultShiftType,
  setTrackSearch,
  isAutomationEnabled,
  locationFilterValue,
  workAreaFilterValue,
  isComplementaryHoursEnabled = false,
}: Props) => {
  const [filteredForms, setFilteredForms] = React.useState<UnassignedShiftForm[]>([])
  const [search, setSearch] = React.useState('')
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()
  const [{ canManageDefaultShifts }] = usePermissions()
  const isEmptyList = forms.length === 0

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const onAddNew = () => {
    trackAddDefaultShiftStart({ daysPreselected: hasSelection, view, from: 'modal' })
    navigate(navigation.shifts.savedShifts.newSavedShift.fullPath())
  }
  const defaultShiftIds = forms.map((form) => +form.get('id').value)

  useUnsafeEffect(() => {
    const shiftIds = new Set(defaultShiftIds)

    if (isAutomationEnabled) {
      setFilteredForms(() => {
        return forms.filter((ds) => {
          if (search) {
            return (
              matchText(search, ds.get('title').value) &&
              (Number(locationFilterValue) === Number(ds.values.location_id) ||
                !ds.values.location_id)
            )
          }
          return (
            Number(locationFilterValue) === Number(ds.values.location_id) || !ds.values.location_id
          )
        })
      })
    } else {
      setFilteredForms(() => {
        return forms.filter((ds) =>
          matchText(search, ds.get('title').value || shiftIds.has(+ds.get('id')))
        )
      })
    }
  }, [isAutomationEnabled, forms, search])

  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { defaultShiftIds },
    skip: !defaultShiftIds.length,
  })

  // TODO: Move out the Search logic into a custom hook, or maybe
  // move the search bar altogether with it.
  //
  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value || '')
    setTrackSearch && setTrackSearch(event.target.value)
  }

  const borderColor = isSelected ? 'viridian1000' : 'grey400'
  const borderRadius: BorderRadiusCorners = {
    all: 'abs008',
  }

  return (
    <Box background="grey200" borderRadius={borderRadius}>
      <Box
        border={{
          all: {
            width: 's2',
            style: 'solid',
            color: borderColor,
          },
        }}
        borderRadius={borderRadius}
        background="grey000White"
      >
        <Box
          alignItems="center"
          borderRadius={borderRadius}
          padding="s16"
          flexDirection="row"
          justifyContent="spaceBetween"
          background="grey000White"
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s4">
            <Icon.Small icon="circle" color="grey400" />
            <Text size="300" weight="semibold" color="grey800">
              {i18n.t('shift_management.add_shift.select_a_saved_shift')}
            </Text>
          </Box>

          <Touchable onClick={onSelectDefaultShiftType}>
            <UnsafeRadioInput selected={isSelected} />
          </Touchable>
        </Box>

        {isSelected && (
          <>
            <Box paddingX="s16">
              <Divider />
            </Box>

            <Box gap="s16" borderRadius={borderRadius} padding="s16">
              {!isEmptyList && (
                <SearchField
                  placeholder={i18n.t('shift_management.add_shift.search_by_title')}
                  onSearch={onSearch}
                  search={search}
                  variant="big"
                />
              )}

              <ScrollableList size="medium">
                <Box gap="s8">
                  {filteredForms.map((form) => {
                    return (
                      <DefaultShiftItem
                        workAreaFilterValue={workAreaFilterValue}
                        locationFilterValue={locationFilterValue}
                        key={form.get('id').value}
                        form={form}
                        plannedBreaks={plannedBreaksCollection.filterByDefaultShift(
                          form.get('id').value
                        )}
                        isAutomationEnabled={isAutomationEnabled}
                        onToggleShift={onToggleShift}
                        isComplementaryHoursEnabled={isComplementaryHoursEnabled}
                      />
                    )
                  })}
                </Box>
              </ScrollableList>
              {canManageDefaultShifts && (
                <>
                  <Box alignItems="center" flexDirection="row" justifyContent="center" gap="s12">
                    {!isEmptyList ? (
                      <>
                        <Box minWidth="s140">
                          <Divider />
                        </Box>
                        {i18n.t('common.or')}
                        <Box minWidth="s140">
                          <Divider />
                        </Box>
                      </>
                    ) : (
                      <>{i18n.t('shift_management.saved_shifts.empty')}</>
                    )}
                  </Box>
                  <Box alignItems="center">
                    <ButtonAddNew onClick={onAddNew} />
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default observer(NewDefaultShiftForm)
